import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, WhatsappLogo } from "@phosphor-icons/react";
import { EASE } from "./shared";
import { WHATSAPP_URL } from "@/App";

const HERO_IMG = "/assets/hero-glass.webp";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onNavigate }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 180]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[94vh] flex flex-col justify-end overflow-hidden bg-slate-900"
    >
      <motion.div style={{ y }} className="absolute inset-0 -bottom-48">
        <img
          src={HERO_IMG}
          alt="Sun-facing glass facade"
          className="w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-slate-950/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-slate-950/30" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 w-full pt-40 pb-14 md:pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-[#7DD3FC] mb-6"
          data-testid="hero-overline"
        >
          Solar-control glass coating · Malta &amp; Gozo
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-heading font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-4xl"
        >
          <MaskedLine delay={0.25}>Cut cooling costs.</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-[#38BDF8]">Keep the view.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-7 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subcopy"
        >
          A transparent heat-rejecting coating applied to your existing glass, for
          hotels, offices and commercial buildings. Not film, no replacement, no
          downtime.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-btn"
            onClick={() => onNavigate("quote")}
            className="bg-[#0284C7] text-white font-semibold px-7 py-4 border border-[#0284C7] hover:bg-[#0369A1] hover:-translate-y-0.5 transition-[background-color,transform] duration-150"
          >
            Book a Free On-Site Demo
          </button>
          <a
            data-testid="hero-whatsapp-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 text-white font-semibold px-7 py-4 border border-white/60 hover:bg-white hover:text-slate-900 transition-[background-color,color] duration-150"
          >
            <WhatsappLogo size={20} weight="fill" />
            WhatsApp Us
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-4 font-mono text-xs tracking-widest text-slate-400"
          data-testid="hero-cta-note"
        >
          (FREE · 20 MIN · NO OBLIGATION)
        </motion.p>

      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => onNavigate("the-physics")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 right-6 md:right-10 text-white/70 hover:text-white transition-colors duration-150"
        aria-label="Scroll down"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block"
        >
          <ArrowDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
}
